import React, { useState, useEffect } from 'react';
import EditModalWrapper from './EditModalWrapper';

const AddCertificateModal = ({
    isOpen,
    onClose,
    certificateData = null,
    onSave,
    isAddMode = true
}) => {
    const buildForm = (data) => ({
        licenseTitle: data?.license_title || data?.title || data?.name || '',
        issuedBy: data?.issued_by || data?.issuing_organization || '',
        issuanceDate: data?.issuance_date || data?.issue_date || '',
        expiryDate: data?.expiry_date || data?.expiration_date || '',
        noEndDate: data?.no_end_date || data?.no_expiry || false,
        description: data?.description || '',
    });

    const [form, setForm] = useState(buildForm(certificateData));

    useEffect(() => {
        if (isOpen) {
            setForm(buildForm(certificateData));
        }
    }, [isOpen, certificateData]);

    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!form.licenseTitle.trim()) {
            alert('License Title is required');
            return;
        }
        if (!form.issuedBy.trim()) {
            alert('Issued By is required');
            return;
        }
        if (!form.issuanceDate) {
            alert('Issuance Date is required');
            return;
        }
        if (!form.noEndDate && !form.expiryDate) {
            alert('Expiry Date is required (or check "No End Date")');
            return;
        }

        setIsSubmitting(true);
        try {
            const certificateEntry = {
                license_title: form.licenseTitle.trim(),
                title: form.licenseTitle.trim(),
                name: form.licenseTitle.trim(),
                issued_by: form.issuedBy.trim(),
                issuing_organization: form.issuedBy.trim(),
                issuance_date: form.issuanceDate,
                issue_date: form.issuanceDate,
                expiry_date: form.noEndDate ? null : form.expiryDate,
                expiration_date: form.noEndDate ? null : form.expiryDate,
                no_end_date: form.noEndDate,
                no_expiry: form.noEndDate,
                description: form.description,
            };

            await onSave(certificateEntry);
            onClose();
        } catch (error) {
            console.error('Error saving certificate:', error);
            alert('Failed to save certificate. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <EditModalWrapper
            isOpen={isOpen}
            onClose={onClose}
            title={isAddMode ? "Add Licenses & Certificates Details" : "Edit Licenses & Certificates Details"}
            onSubmit={handleSubmit}
            isSubmitting={isSubmitting}
            submitLabel={isAddMode ? "Add" : "Update"}
            size="md"
        >
            <div className="space-y-5">
                {/* License Title */}
                <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">
                        License Title <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="text"
                        value={form.licenseTitle}
                        onChange={(e) => setForm({ ...form, licenseTitle: e.target.value })}
                        className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all bg-slate-50 text-slate-900"
                        placeholder="Enter"
                        required
                    />
                </div>

                {/* Issued By */}
                <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">
                        Issued By <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="text"
                        value={form.issuedBy}
                        onChange={(e) => setForm({ ...form, issuedBy: e.target.value })}
                        className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all bg-slate-50 text-slate-900"
                        placeholder="Enter"
                        required
                    />
                </div>

                {/* Issuance Date & Expiry Date */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1.5">
                            Issuance Date <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="date"
                            value={form.issuanceDate}
                            onChange={(e) => setForm({ ...form, issuanceDate: e.target.value })}
                            className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all text-slate-900"
                            required
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1.5">
                            Expiry Date <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="date"
                            value={form.expiryDate}
                            onChange={(e) => setForm({ ...form, expiryDate: e.target.value })}
                            disabled={form.noEndDate}
                            className={`w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all text-slate-900 ${form.noEndDate ? 'bg-slate-100 cursor-not-allowed' : ''}`}
                        />
                    </div>
                </div>

                {/* No End Date checkbox */}
                <div className="flex justify-end">
                    <label className="flex items-center gap-2 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={form.noEndDate}
                            onChange={(e) => setForm({ ...form, noEndDate: e.target.checked, expiryDate: e.target.checked ? '' : form.expiryDate })}
                            className="h-4 w-4 text-blue-600 rounded border-slate-300 focus:ring-blue-500"
                        />
                        <span className="text-sm text-slate-600">No End Date</span>
                        <span className="text-blue-500 text-sm">ⓘ</span>
                    </label>
                </div>

                {/* Description */}
                <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">
                        Description
                    </label>
                    <textarea
                        value={form.description}
                        onChange={(e) => setForm({ ...form, description: e.target.value })}
                        rows={4}
                        maxLength={512}
                        className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all resize-none bg-slate-50 text-slate-900"
                        placeholder="Description"
                    />
                    <div className="text-right text-xs text-slate-400 mt-1">{512 - form.description.length}</div>
                </div>
            </div>
        </EditModalWrapper>
    );
};

export default AddCertificateModal;
